/**
 * Platform detection utilities for Native Bridge
 *
 * This module provides utilities for detecting the current platform (iOS, Android, or Web)
 * and checking platform-specific capabilities. It uses a caching mechanism to avoid
 * repeated native bridge calls for better performance.
 */

import { NativeBridge } from "./index"
import { addLogError } from "../logger"

export type Platform = "ios" | "android" | "web"

export interface PlatformInfo {
  platform: Platform
  version: string
  osVersion: string
  device: string
  bridgeVersion: string
}

export interface PlatformSupport {
  bluetooth: boolean
  workoutKit?: boolean // iOS only
  healthKit?: boolean // iOS only
  appleWatch?: boolean // iOS only
  appleHealth?: boolean // iOS only
  googleFit?: boolean // Android only
  /** Multi-device lactometer bridge: registry, active-device switch, EN605
   *  config writes (time/zones/notes). Absent on older native builds, which
   *  keep the single-device lactometer UI. */
  lactateV2?: boolean
  platform: Platform
}

let cachedPlatformInfo: PlatformInfo | null = null
let cachedPlatformSupport: PlatformSupport | null = null

/**
 * Gets platform information from the native bridge
 *
 * @returns Platform information including OS version, device info, and bridge version
 *          Returns null if native bridge is not available (web environment)
 *
 * @example
 * const info = await getPlatformInfo()
 * if (info?.platform === 'ios') {
 *   // iOS-specific logic
 * }
 */
export async function getPlatformInfo(): Promise<PlatformInfo | null> {
  if (cachedPlatformInfo) {
    return cachedPlatformInfo
  }

  if (!NativeBridge.isAvailable()) {
    return null
  }

  try {
    const nativeBridge = NativeBridge.getInstance()
    const info = await nativeBridge.request<Record<string, never>, PlatformInfo>("system.getInfo", {})

    cachedPlatformInfo = {
      platform: info.platform,
      version: info.version,
      osVersion: info.osVersion,
      device: info.device,
      bridgeVersion: info.bridgeVersion,
    }

    return cachedPlatformInfo
  } catch (error) {
    addLogError("Failed to get platform info:", error)
    return null
  }
}

/**
 * Gets platform support capabilities
 *
 * @returns Object containing boolean flags for supported features on the current platform
 *          Returns null if native bridge is not available (web environment)
 *
 * @example
 * const support = await getPlatformSupport()
 * if (support?.bluetooth) {
 *   // Bluetooth is supported
 * }
 */
export async function getPlatformSupport(): Promise<PlatformSupport | null> {
  if (cachedPlatformSupport) {
    return cachedPlatformSupport
  }

  if (!NativeBridge.isAvailable()) {
    return null
  }

  try {
    const nativeBridge = NativeBridge.getInstance()
    const support = await nativeBridge.request<Record<string, never>, PlatformSupport>("system.checkSupport", {})

    cachedPlatformSupport = {
      bluetooth: support.bluetooth,
      workoutKit: support.workoutKit,
      healthKit: support.healthKit,
      appleWatch: support.appleWatch,
      appleHealth: support.appleHealth,
      googleFit: support.googleFit,
      lactateV2: support.lactateV2,
      platform: support.platform,
    }

    return cachedPlatformSupport
  } catch (error) {
    addLogError("Failed to get platform support:", error)
    return null
  }
}

/**
 * Checks if the current platform is iOS
 *
 * @returns true if running on iOS platform, false otherwise
 *
 * @example
 * if (await isIOS()) {
 *   // Enable iOS-specific features
 * }
 */
export async function isIOS(): Promise<boolean> {
  const info = await getPlatformInfo()
  return info?.platform === "ios"
}

/**
 * Checks if the current platform is Android
 *
 * @returns true if running on Android platform, false otherwise
 *
 * @example
 * if (await isAndroid()) {
 *   // Enable Android-specific features
 * }
 */
export async function isAndroid(): Promise<boolean> {
  const info = await getPlatformInfo()
  return info?.platform === "android"
}

/**
 * Checks if a specific feature is supported on the current platform
 *
 * @param feature - The feature to check (e.g., 'bluetooth', 'workoutKit', 'healthKit')
 * @returns true if the feature is supported, false otherwise
 *
 * @example
 * if (await isFeatureSupported('bluetooth')) {
 *   // Initialize Bluetooth functionality
 * }
 */
export async function isFeatureSupported(feature: keyof PlatformSupport): Promise<boolean> {
  const support = await getPlatformSupport()
  return support?.[feature] === true
}

/**
 * Platform-specific feature flags
 *
 * Constants for commonly used platform features to avoid typos
 * and provide better type safety when checking feature support
 *
 * @example
 * if (await isFeatureSupported(PlatformFeatures.APPLE_WATCH)) {
 *   // Apple Watch feature is available
 * }
 */
export const PlatformFeatures = {
  APPLE_WATCH: "appleWatch",
  APPLE_HEALTH: "appleHealth",
  WORKOUT_KIT: "workoutKit",
  HEALTH_KIT: "healthKit",
  GOOGLE_FIT: "googleFit",
  BLUETOOTH: "bluetooth",
  LACTATE_V2: "lactateV2",
} as const

/**
 * Checks whether the native app exposes the multi-device lactometer bridge
 * (registry, active-device switch, EN605 config writes). Returns false on the
 * web and on older native builds, where the single-device UI is used instead.
 */
export async function isLactateV2Supported(): Promise<boolean> {
  return isFeatureSupported("lactateV2")
}

/**
 * Helper to check multiple features at once
 *
 * @param features - Array of feature names to check
 * @returns Object mapping feature names to their availability status
 *
 * @example
 * const features = await checkFeatures(['bluetooth', 'healthKit', 'workoutKit'])
 * if (features.bluetooth && features.healthKit) {
 *   // Both Bluetooth and HealthKit are available
 * }
 */
export async function checkFeatures(features: string[]): Promise<Record<string, boolean>> {
  const support = await getPlatformSupport()
  if (!support) return {}

  return features.reduce(
    (acc, feature) => {
      acc[feature] = support[feature as keyof PlatformSupport] === true
      return acc
    },
    {} as Record<string, boolean>,
  )
}
